import { makeAutoObservable, type IReactionDisposer, reaction } from 'mobx'
import { type PageLayoutStore } from 'shared/layout'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type CallModalStore } from 'widgets/CallModal'
import { ConversationMessagesStore } from 'widgets/ConversationMessages'
import { ConversationNew, ConversationNewSearchStore } from 'widgets/ConversationNew'
import type { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type ContactCreateModalStore } from 'widgets/ContactCreateModal'
import { type ConversationSearchStore } from 'pages/conversations/ui/ConversationHeaderSearch'
import { ConversationListStore } from 'pages/conversations/ui/ConversationList'
import { type ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { openProfileSearch } from 'pages/conversations/pages/search'

export class ConversationsPageStore {
  private readonly _conversationMessagesStore: ConversationMessagesStore
  private readonly _conversationListStore: ConversationListStore
  private readonly _conversationNewSearchStore: ConversationNewSearchStore
  private _disposeContactsDetails: IReactionDisposer | null = null

  constructor(
    private _pageLayoutStore: PageLayoutStore,
    private _callModalStore: CallModalStore,
    private _conversationMenuStore: ConversationMenuStore,
    private _conversationSearchStore: ConversationSearchStore,
    private _contactsDetailsManageStore: ContactsDetailsManageStore,
    private _contactCreateModalStore: ContactCreateModalStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._conversationNewSearchStore = new ConversationNewSearchStore(this._entities, {
      includeRCS: true,
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
    })
    this._conversationMessagesStore = new ConversationMessagesStore(
      this._entities,
      this._pageLayoutStore
    )
    this._conversationListStore = new ConversationListStore(
      this._pageLayoutStore,
      this._conversationSearchStore,
      this._entities
    )

    this._conversationNewSearchStore.setConfig({
      conversationMessagesStore: this._conversationMessagesStore,
      onChangeAlert: (item) => {
        this._contactsDetailsManageStore.contactsDetailsStore?.setAlert(item)
      },
      changeInbox: (inbox) => {
        this._conversationMessagesStore.setInboxId(inbox.id)
        this._contactsDetailsManageStore.contactsDetailsStore?.setInboxId(inbox.id)
      },
      autoSelectInbox: true,
      onClose: () => {
        this._pageLayoutStore.toStartContainer()
      },
    })

    this._conversationListStore.setConfig({
      onChangeLoadingConversations: (value) => {
        this._conversationMessagesStore.setLoadingConversations(value)
      },
    })

    this._conversationMessagesStore.setConfig({
      callModalStore: this._callModalStore,
      onBack: () => {
        if (this._conversationSearchStore.hasSearchParams) {
          this._conversationSearchStore.setHide(false)
        }
      },
      onStartSendProcess: (message, scheduledData, conversationId, withUndo, onSendSuccess) => {
        this._conversationNewSearchStore.checkError()

        this._conversationNewSearchStore.handleCreateConversation(
          scheduledData,
          false,
          message,
          withUndo,
          onSendSuccess
        )
      },
      onCheckSendAction: () => {
        if (!this._conversationMessagesStore.isConversationNew) return false

        return this._conversationNewSearchStore.checkError()
      },
      onCreateConversation: async (scheduledData, isDoubleOptInButton) => {
        await this._conversationNewSearchStore.handleCreateConversation(
          scheduledData,
          isDoubleOptInButton
        )
      },
      onCloseConversation: async () => {
        const conversationId = this._conversationMessagesStore.conversationId
        const conversation = this.conversationStore.getItem(conversationId)

        if (!conversation) return

        if (conversation.isClosed) {
          await this.conversationListStore.onOpen(conversationId)
        } else {
          await this.conversationListStore.onManualClose(conversationId)
        }
      },
      renderCustomHeader: () => {
        return <ConversationNew store={this._conversationNewSearchStore} />
      },
    })

    this._callModalStore.setConfig({
      setHideTrialAlertNotForOwner: this._conversationMessagesStore.setHideTrialAlertNotForOwner,
    })

    this._conversationSearchStore.setConfig({
      onLoadConversations: () => {
        this._conversationListStore.load()
      },
      onSearchConversations: () => {
        this._conversationListStore.searchConversations()
      },
      onResetConversations: () => {
        this._conversationListStore.reset(false)
      },
      onChangeSelectedSearch: (value) => {
        this._conversationMessagesStore.setSelectedSearch(value)
      },
      onChangeHasSearchParams: (value) => {
        this._conversationMessagesStore.setHasSearchParams(value)
      },
    })

    this._conversationMenuStore.setConfig({
      setConversationsLoading: this._conversationListStore.setLoading,
      resetConversations: this._conversationListStore.reset,
      resetConversationsReactions: this._conversationListStore.resetReactions,
      setConversationFocusItem: this._conversationListStore.setFocusItemById,
      reloadConversations: () => {
        this._conversationListStore.reset(true)
        this._conversationListStore.load()
      },
    })

    this._contactsDetailsManageStore.setConfig({
      emitOpenConversationSignal: this.emitOpenConversationSignal,
    })

    this._contactCreateModalStore.setConfig({
      emitOpenConversationSignal: this.emitOpenConversationSignal,
    })

    this.init()
    this.emitOpenConversationSignal()
  }

  init = () => {
    this._contactsDetailsManageStore.init()
    this.reactionContactsDetails()
  }

  reset = () => {
    this._entities.contactsStore.setBlockContactModalCallbacks(null)
    this._disposeContactsDetails?.()
    this._contactsDetailsManageStore.reset()
  }

  reactionContactsDetails = () => {
    this._disposeContactsDetails?.()
    this._disposeContactsDetails = reaction(
      () => this.conversationStore.currentItem,
      (conversation) => {
        if (this._contactsDetailsManageStore.contactsDetailsStore?.isVariantOpenConversations) {
          return
        }

        if (!conversation) {
          this._entities.contactsStore.setBlockContactModalCallbacks(null)
          this._contactsDetailsManageStore.resetContactsDetails()

          return
        }

        if (this._contactsDetailsManageStore.contactsDetailsStore) {
          this._contactsDetailsManageStore.contactsDetailsStore.setConversationId(conversation.id)
          this._contactsDetailsManageStore.contactsDetailsStore.setContactId(
            conversation.contact_id
          )

          return
        }

        this._entities.contactsStore.setBlockContactModalCallbacks({})

        this._contactsDetailsManageStore.setContactsDetails({
          conversationId: conversation.id,
          contactId: conversation.contact_id,
          profileCardProps: {
            enableCall: true,
          },
          variant: 'conversation',
          onUpdateContact: (contact) => {
            if (!this._contactCreateModalStore.hasOpenModal()) return
            if (contact?.id !== -1) {
              this._contactCreateModalStore.handleContactUpdate(contact)
            }
          },
          resetAlert: () => {
            if (!this._contactCreateModalStore.hasOpenModal()) return

            this._conversationNewSearchStore.resetAlert()
          },
          onChangeIsFirstEditMode: (value) => {
            this._contactCreateModalStore.setIsFirstEditMode(value)
            this._conversationNewSearchStore.setIsFirstEditMode(value)
          },
          getParamsCreateSummaryAnswer:
            this._conversationMessagesStore.getParamsCreateSummaryAnswer,
        })
      },
      {
        fireImmediately: true,
      }
    )
  }

  emitOpenConversationSignal = () => {
    const signal = this.conversationStore.signal

    if (!signal) return
    if (signal.type === 'open_new_conversation') {
      this._conversationNewSearchStore.handleAddItem(signal.payload.contact)

      const inbox = signal.payload.inbox

      if (inbox) {
        const inboxId = inbox.id
        const numberId = signal.payload.numberId || inbox.numberId

        this._conversationNewSearchStore.handleSetActiveNumber(inboxId, numberId)
      }
    }

    if (signal.type === 'open_new_group_conversation') {
      signal.payload.contacts?.forEach((contact) => {
        this._conversationNewSearchStore.handleAddItem(contact)
      })

      const inbox = signal.payload.inbox
      if (inbox) {
        const inboxId = inbox.id
        const numberId = signal.payload.numberId || inbox.numberId

        this._conversationNewSearchStore.handleSetActiveNumber(inboxId, numberId)
      }
    }

    if (signal.type === 'open_group_conversation') {
      if (this._entities.featureFlagsStore.search_results_page) {
        openProfileSearch({
          contactId: signal.payload.contact.id,
        })
      } else {
        this._conversationSearchStore.selectTypeItem(signal.payload.contact)
        this._conversationSearchStore.selectType(SearchTypesEnum.contacts)
        this._conversationSearchStore.onOpen()
      }
    }

    this.conversationStore.clearSignal()
  }

  get entities() {
    return this._entities
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  get conversationMenuStore() {
    return this._conversationMenuStore
  }

  get callModalStore() {
    return this._callModalStore
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get conversationSearchStore() {
    return this._conversationSearchStore
  }

  get conversationListStore() {
    return this._conversationListStore
  }

  get conversationMessagesStore() {
    return this._conversationMessagesStore
  }

  get conversationNewSendFromInboxId() {
    return this._conversationNewSearchStore.sendFromStore.inbox_id
  }
}
